/*
 *  Developed by CQ Inversiones SAS. Copyright (c) 2024. All rights reserved.
 *  Desarrollado por CQ Inversiones SAS. Copyright (c) 2024. Todos los derechos reservados.
 */

/*
 * Project:      mkad-frontend
 * Developed by: Juan David Pelaez
 * Date:         6/05/24 - 23:42:32
 * Module name:  mkad-table.const.ts
 * File name:    mkad-table.const.ts
 * IDE:          WebStorm
 */
import {ColumnMode, SelectionType, SortType} from '@swimlane/ngx-datatable';
import {
  TableColumnEnum,
  TableActionConfigInterface,
  TableBasicConfigInterface,
  TableCssClassConfigInterface,
  TableExtraConfigInterface,
  TableImageConfigInterface,
  TableMessageConfigInterface, TableIconInterface, TableActionEnum,
} from '@shared/components/table/utils';


export const MKAD_STATUS_CLASS: Record<string, string> = {
  '0': 'status__success',
  '1': 'status__alert',
  '5': 'status__warning',
  '3': 'status__danger',
  '10': 'status__comm',
  '11': 'status__sensor',
};

export const TABLE_ACTION_TOOLTIP: Record<TableActionEnum, string> = {
  [TableActionEnum.update]: 'app.shared.tables.action.update.tooltip',
  [TableActionEnum.delete]: 'app.shared.tables.action.delete.tooltip',
  [TableActionEnum.customBasic]: 'app.shared.tables.action.customBasic.tooltip',
  [TableActionEnum.customGroups]: 'app.shared.tables.action.customGroups.tooltip',
  [TableActionEnum.customParams]: 'app.shared.tables.action.customParams.tooltip',
  [TableActionEnum.customPorts]: 'app.shared.tables.action.customPorts.tooltip',
  [TableActionEnum.add]: 'app.shared.tables.action.add.tooltip',
  [TableActionEnum.limits]: 'app.shared.tables.action.limits.tooltip',
  [TableActionEnum.swap]: 'app.shared.tables.action.swap.tooltip',
  [TableActionEnum.userAdd]: 'app.shared.tables.action.useAdmin.tooltip',
  [TableActionEnum.row]: 'app.shared.tables.action.row.tooltip',
}


/**
 * @description
 * icons support
 * @enum
 */
export enum TableIconEnum {
  pen = 'pen',
  trash = 'trash',
  bell = 'bell',
  arrowsRotate = 'arrowsRotate',
  add = 'add',
  minus = 'minus',
  circleCheck = 'circleCheck',
  circleClose = 'circleClose',
  mail = 'mail',
  whatsapp = 'whatsapp',
  facebook = 'facebook',
  twitter = 'twitter',
  telegram = 'telegram',
  insta = 'insta',
  messenger = 'messenger',
  sms = 'sms',
  brain = 'brain',
  houseLap = 'houseLap',
  chartLineUp = 'chartLineUp',
  image = 'image',
  imageSlash = 'imageSlash',
  file = 'file',
  fileSlash = 'fileSlash',
  groups = 'groups',
  params = 'params',
  ports = 'ports',
  limits = 'limits',
  osCommand = 'osCommand',
  pwmCommand = 'pwmCommand',
  operationPort = 'operationPort',
  model = 'model',
  sensor = 'sensor',
  custom = 'custom',
  swap = 'swap',
  gem = 'gem',
  diamond4 = 'diamond4',
  certificate = 'certificate',
  seal = 'seal',
  circle = 'circle',
  user = 'user',
  userAdmin = 'userAdmin'
}

/**
 * @description
 * Icons class allowed in the library
 * @constant
 */
export const TableIconsClass: Record<TableIconEnum, string> = {
  pen: 'pen',
  trash: 'trash-can',
  arrowsRotate: 'rotate',
  add: 'plus',
  minus: 'minus',
  circleCheck: 'circle-check',
  circleClose: 'circle-xmark',
  mail: 'envelope',
  whatsapp: 'whatsapp',
  facebook: 'facebook',
  telegram: 'telegram',
  insta: 'instagram',
  twitter: 'x-twitter',
  messenger: 'facebook-messenger',
  sms: 'comment-sms',
  brain: 'brain-circuit',
  houseLap: 'house-laptop',
  chartLineUp: 'chart-mixed-up-circle-currency',
  image: 'image',
  imageSlash: 'image-slash',
  bell: 'bell',
  file: 'file-lines',
  fileSlash: 'file-slash',
  groups: 'users-medical',
  params: 'list-tree',
  ports: 'microchip',
  limits: 'radar',
  osCommand: 'square-terminal',
  pwmCommand: 'circle-bolt',
  operationPort: 'megaport',
  model: 'circle-m',
  sensor: 'circle-s',
  custom: 'shield-check',
  swap: 'swap',
  diamond4: 'diamond',
  gem: 'gem',
  certificate: 'certificate',
  seal: 'seal',
  circle: 'circle',
  user : 'user',
  userAdmin : 'user-crown'
};

/**
 * @description
 * maps of icons class and pack
 */
export const TABLE_ICONS_MAP: Record<TableIconEnum, TableIconInterface> = {
  [TableIconEnum.add]: {
    icon: TableIconsClass.add,
    pack: 'far',
  },
  [TableIconEnum.minus]: {
    icon: TableIconsClass.minus,
    pack: 'far',
  },
  [TableIconEnum.arrowsRotate]: {
    icon: TableIconsClass.arrowsRotate,
    pack: 'far',
  },
  [TableIconEnum.pen]: {
    icon: TableIconsClass.pen,
    pack: 'far',
  },
  [TableIconEnum.sms]: {
    icon: TableIconsClass.sms,
    pack: 'far',
  },
  [TableIconEnum.facebook]: {
    icon: TableIconsClass.facebook,
    pack: 'fab',
  },
  [TableIconEnum.messenger]: {
    icon: TableIconsClass.messenger,
    pack: 'fab',
  },
  [TableIconEnum.insta]: {
    icon: TableIconsClass.insta,
    pack: 'fab',
  },
  [TableIconEnum.telegram]: {
    icon: TableIconsClass.telegram,
    pack: 'fab',
  },
  [TableIconEnum.twitter]: {
    icon: TableIconsClass.twitter,
    pack: 'fab',
  },
  [TableIconEnum.whatsapp]: {
    icon: TableIconsClass.whatsapp,
    pack: 'fab',
  },
  [TableIconEnum.circleCheck]: {
    icon: TableIconsClass.circleCheck,
    pack: 'far',
  },
  [TableIconEnum.circleClose]: {
    icon: TableIconsClass.circleClose,
    pack: 'far',
  },
  [TableIconEnum.mail]: {
    icon: TableIconsClass.mail,
    pack: 'far',
  },
  [TableIconEnum.trash]: {
    icon: TableIconsClass.trash,
    pack: 'far',
  },
  [TableIconEnum.brain]: {
    icon: TableIconsClass.brain,
    pack: 'far',
  },
  [TableIconEnum.houseLap]: {
    icon: TableIconsClass.houseLap,
    pack: 'far',
  },
  [TableIconEnum.chartLineUp]: {
    icon: TableIconsClass.chartLineUp,
    pack: 'far',
  },
  [TableIconEnum.image]: {
    icon: TableIconsClass.image,
    pack: 'far',
  },
  [TableIconEnum.imageSlash]: {
    icon: TableIconsClass.imageSlash,
    pack: 'far',
  },
  [TableIconEnum.bell]: {
    icon: TableIconsClass.bell,
    pack: 'far',
  },
  [TableIconEnum.file]: {
    icon: TableIconsClass.file,
    pack: 'far',
  },
  [TableIconEnum.fileSlash]: {
    icon: TableIconsClass.fileSlash,
    pack: 'far',
  },
  [TableIconEnum.groups]: {
    icon: TableIconsClass.groups,
    pack: 'far',
  },
  [TableIconEnum.params]: {
    icon: TableIconsClass.params,
    pack: 'far',
  },
  [TableIconEnum.ports]: {
    icon: TableIconsClass.ports,
    pack: 'far',
  },
  [TableIconEnum.limits]: {
    icon: TableIconsClass.limits,
    pack: 'far',
  },
  [TableIconEnum.osCommand]: {
    icon: TableIconsClass.osCommand,
    pack: 'far',
  },
  [TableIconEnum.pwmCommand]: {
    icon: TableIconsClass.pwmCommand,
    pack: 'far',
  },
  [TableIconEnum.operationPort]: {
    icon: TableIconsClass.operationPort,
    pack: 'far',
  },
  [TableIconEnum.model]: {
    icon: TableIconsClass.model,
    pack: 'far',
  },
  [TableIconEnum.sensor]: {
    icon: TableIconsClass.sensor,
    pack: 'far',
  },
  [TableIconEnum.custom]: {
    icon: TableIconsClass.custom,
    pack: 'far',
  },
  [TableIconEnum.swap]: {
    icon: TableIconsClass.swap,
    pack: 'far'
  },
  [TableIconEnum.diamond4]: {
    icon: TableIconsClass.diamond4,
    pack: 'far',
  },
  [TableIconEnum.gem]: {
    icon: TableIconsClass.gem,
    pack: 'far',
  },
  [TableIconEnum.certificate]: {
    icon: TableIconsClass.certificate,
    pack: 'far',
  },
  [TableIconEnum.seal]: {
    icon: TableIconsClass.seal,
    pack: 'far',
  },
  [TableIconEnum.circle]: {
    icon: TableIconsClass.circle,
    pack: 'far',
  },
  [TableIconEnum.user]: {
    icon: TableIconsClass.user,
    pack: 'far',
  },
  [TableIconEnum.userAdmin]: {
    icon: TableIconsClass.userAdmin,
    pack: 'far',
  }
};

/**
 * @description
 * Table column icon width size
 */
export const TABLE_COLUMN_ICON_WIDTH = 50;

/**
 * @description
 * Table row height
 */
export const TABLE_ROW_HEIGHT = 35;

/**
 * @description
 * Table footer height
 */
export const TABLE_FOOTER_HEIGHT = 28;

/**
 * @description
 * Table header height
 */
export const TABLE_HEADER_HEIGHT = 37;

/**
 * @description
 * children table key
 */
export const TABLE_CHILDREN_KEY = 'children';

/**
 * @description
 * Move children table row
 */
export const TABLE_CHILDREN_ROW_OFFSET = 110;

/**
 * @description
 * Table children row height
 */
export const TABLE_CHILDREN_ROW_HEIGHT = 40;

/**
 * @description
 * Table limit
 */
export const TABLE_LIMIT = 5;

/**
 * @description
 * Default tag config
 */
export const DEFAULT_TAG_CONFIG: TableBasicConfigInterface = {
  activeLabel: 'Active',
  activeCssClass: 'active',
  inactiveLabel: 'Inactive',
  inactiveCssClass: 'inactive',
};

/**
 * @description
 * Default check config
 */
export const DEFAULT_CHECK_CONFIG: TableCssClassConfigInterface = {
  activeCssClass: 'active',
  inactiveCssClass: 'inactive',
};

/**
 * @description
 * Default image config
 */
export const DEFAULT_IMAGE_CONFIG: TableImageConfigInterface = {
  alternativeText: 'image in the row of the table',
  noImageText: 'N/A',
  error: 'Image field has error',
};

/**
 * @description
 * Default message config
 */
export const DEFAULT_MESSAGE_CONFIG: TableMessageConfigInterface = {
  empty: 'No data to display',
  error: 'An error occurred displaying the data',
};

/**
 * @description
 * Default action config
 */
export const DEFAULT_ACTION_CONFIG: TableActionConfigInterface = {
  header: 'Actions',
  data: [],
};

/**
 * @description
 * Default table config
 */
export type DefaultTableField = 'header' | 'footer' | 'row' | 'limit';
export const DEFAULT_TABLE_CONFIG: Record<DefaultTableField, number> = {
  header: TABLE_HEADER_HEIGHT,
  footer: TABLE_FOOTER_HEIGHT,
  row: TABLE_ROW_HEIGHT,
  limit: TABLE_LIMIT,
};

/**
 * @description
 * Default extra config
 */
export const DEFAULT_EXTRA_CONFIG: TableExtraConfigInterface = {
  columnMode: ColumnMode.flex,
  sortType: SortType.single,
  selection: SelectionType.single,
  image: DEFAULT_IMAGE_CONFIG,
  check: DEFAULT_CHECK_CONFIG,
  tag: DEFAULT_TAG_CONFIG,
  messages: DEFAULT_MESSAGE_CONFIG,
  actions: DEFAULT_ACTION_CONFIG,
  clickableRow: false,
};

/**
 * @description
 * Returns text view
 */
export const isViewText = (type: TableColumnEnum) => {
  return type === TableColumnEnum.text;
};
/**
 * @description
 * Returns dual text view
 * @param type
 */
export const isViewTextDuo = (type: TableColumnEnum) => {
  return type === TableColumnEnum.textDual;
};
/**
 * @description
 * Returns the image view
 * @param type
 */
export const isViewImage = (type: TableColumnEnum) => {
  return type === TableColumnEnum.image;
};
/**
 * @description
 * Returns the tag view
 * @param type
 */
export const isViewTag = (type: TableColumnEnum) => {
  return type === TableColumnEnum.tag;
};
/**
 * @description
 * Returns the icon view
 * @param type
 */
export const isViewIcon = (type: TableColumnEnum) => {
  return type === TableColumnEnum.icon;
};
/**
 * @description
 * Returns the icon duo view
 * @param type
 */
export const isViewIconDuo = (type: TableColumnEnum) => {
  return type === TableColumnEnum.iconDual;
};
/**
 * @description
 * Returns the icon group
 * @param type
 */
export const isViewIconGroup = (type: TableColumnEnum) => {
  return type === TableColumnEnum.iconGroup;
};
/**
 * @description
 * Returns the icon enum
 * @param type
 */
export const isViewIconEnum = (type: TableColumnEnum) => {
  return type === TableColumnEnum.iconEnum;
};

/**
 * @description
 * Returns the initials
 * @param type
 */
export const isViewInitials = (type: TableColumnEnum) => {
  return type === TableColumnEnum.initialsGroup;
};

/**
 * @description
 * Returns the view phone
 * @param type
 */
export const isViewPhone = (type: TableColumnEnum) => {
  return type === TableColumnEnum.phone;
};

/**
 * @description
 * Returns the view enum
 * @param type
 */
export const isViewEnum = (type: TableColumnEnum) => {
  return type === TableColumnEnum.enum;
};

/**
 * @description
 * Return type validation
 * @param field
 */
export const validArrayType = (field: string | string[]): boolean => {
  return Array.isArray(field);
};

/**
 * @description
 * Return validation of type string
 * @param field
 */
export const validStringType = (field: string | Record<string, unknown>): boolean => {
  return typeof field === 'string';
};

/**
 * @description
 * Returns the assertion string
 * @param field
 */
export const assertionString = (field: string | string[]): string => {
  return <string>field;
};

/**
 * @description
 * Returns the assertion string
 * @param field
 */
export const assertionIcon = (field: string ): TableIconEnum => {
  return <TableIconEnum>field;
};

/**
 * @description
 * Returns the assertion array
 * @param field
 */
export const assertionArray = (field: unknown): string[] => {
  return <string[]>field;
};

/**
 * @description
 * Returns the assertion column mode
 * @param mode
 */
export const assertionColumnMode = (mode?: ColumnMode): ColumnMode => {
  return <ColumnMode>mode;
};

/**
 * @description
 * Returns the assertion sort mode
 * @param type
 */
export const assertionSortMode = (type?: SortType): SortType => {
  return <SortType>type;
};

/**
 * @description
 * Returns the string it receives or an empty string if nothing is received.
 * @param value
 */
export const parseString = (value?: string): string => <string>value;
